/**
 * Billing access rules — the single place the dashboard decides what a host
 * may do, from the row get_user_billing_info returns.
 *
 * The SQL is the truth (profile_has_access / portal_open, migration
 * 20261026200000_billing_lifecycle). These helpers only mirror it for the UI:
 *
 *   full    — admin, or has_access (active / cancel_scheduled / past_due /
 *             running trial / gift or beta not yet ended). Everything works.
 *   grace   — the plan ended (cancelled with a 30-day grace, or a gift whose
 *             end date passed before the daily job ran). The host sees and
 *             edits everything, guest portals keep working, but nothing NEW
 *             can be created (properties, units, bookings). The server
 *             enforces that; the UI explains it and disables the buttons.
 *   blocked — suspended / expired / never paid / account pending deletion.
 *             Only Billing, Account and Support open; guest portals are off
 *             (except for "never paid", which has no portals anyway).
 *
 * Everything here is pure (no React, no Supabase) so it is unit-tested.
 */
import type { BillingInfo } from '@/hooks/useSubscription';
import { PRICING_TIERS, type PricingTierId } from './stripe';

export type AccessLevel = 'full' | 'grace' | 'blocked';

/** Why a host is not on full access. */
export type BlockReason =
  | 'grace'             // plan ended, 30 days of read/edit, portals open
  | 'suspended'         // grace over (or legacy expired) — portals closed
  | 'deletion_pending'  // host asked to delete the account
  | 'no_plan';          // never paid / trial over without data / unknown

/** Pages a host without full access can still open. */
export const LIMITED_ACCESS_PATHS = [
  '/dashboard/billing',
  '/dashboard/account',
  '/dashboard/support',
] as const;

const LIVE_STATUSES = ['active', 'cancel_scheduled', 'past_due'];
const CLOSED_STATUSES = ['suspended', 'expired'];
const DAY_MS = 24 * 60 * 60 * 1000;

function toTime(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Does the profile have FULL access? Uses the server's has_access when the
 * RPC returns it; otherwise (old RPC still deployed) the status rule.
 */
export function effectiveHasAccess(info: BillingInfo | null | undefined, now: Date = new Date()): boolean {
  if (!info) return false;
  if (info.role === 'admin') return true;
  if (typeof info.has_access === 'boolean') return info.has_access;
  if (info.deleted_at) return false;
  const status = String(info.subscription_status ?? 'free');
  if (LIVE_STATUSES.includes(status)) return true;
  const trialEnd = toTime(info.trial_ends_at);
  return status === 'free' && trialEnd !== null && trialEnd > now.getTime();
}

/** Are the host's guest portals open? (server: portal_open) */
export function effectivePortalOpen(info: BillingInfo | null | undefined): boolean {
  if (!info) return false;
  if (typeof info.portal_open === 'boolean') return info.portal_open;
  if (info.deleted_at) return false;
  return !CLOSED_STATUSES.includes(String(info.subscription_status ?? ''));
}

/** Why the host is not on full access (null = full access). */
export function blockReason(info: BillingInfo | null | undefined, now: Date = new Date()): BlockReason | null {
  if (!info) return 'no_plan';
  if (effectiveHasAccess(info, now)) return null;
  if (info.deleted_at) return 'deletion_pending';
  const status = String(info.subscription_status ?? 'free');
  if (CLOSED_STATUSES.includes(status)) return 'suspended';
  if (!effectivePortalOpen(info)) return 'suspended';
  // Plan ended but portals still open → the 30-day grace. A gift/admin plan
  // whose end date passed before tonight's lifecycle job is still 'active'
  // here — it gets the same grace once the job runs, so treat it the same.
  if (status === 'cancelled' || LIVE_STATUSES.includes(status)) return 'grace';
  // A trial that ended for a host who already has units: the nightly job
  // turns it into cancelled + grace (trial end + 30 days) — same treatment now.
  if (status === 'free' && trialEndedWithUnits(info, now)) return 'grace';
  return 'no_plan';
}

function trialEndedWithUnits(info: BillingInfo, now: Date): boolean {
  const trialEnd = toTime(info.trial_ends_at);
  // Same rule as the nightly job: the host owns at least one (non-trashed)
  // property. Older RPCs have no property_count → active units as the proxy.
  const owns = typeof info.property_count === 'number' ? info.property_count > 0 : (info.current_units ?? 0) > 0;
  return trialEnd !== null && trialEnd <= now.getTime() && owns;
}

export function accessLevel(info: BillingInfo | null | undefined, now: Date = new Date()): AccessLevel {
  const reason = blockReason(info, now);
  if (reason === null) return 'full';
  if (reason === 'grace') return 'grace';
  return 'blocked';
}

/** Last day the guest portals stay open during the grace period. */
export function graceEndsAt(info: BillingInfo | null | undefined, now: Date = new Date()): string | null {
  if (!info) return null;
  if (info.grace_until) return info.grace_until;
  const status = String(info.subscription_status);
  // Before the nightly job has set grace_until, predict what it will set:
  // GREATEST(end + 30 days, now + 7 days) — never less than a week's notice.
  const predicted = (endMs: number) =>
    new Date(Math.max(endMs + 30 * DAY_MS, now.getTime() + 7 * DAY_MS)).toISOString();
  // Trial ended: from the trial end.
  const trialEnd = toTime(info.trial_ends_at);
  if (status === 'free' && trialEnd !== null) return predicted(trialEnd);
  // Beta tester (no Stripe): from beta_expires_at.
  const betaEnd = toTime(info.beta_expires_at);
  if (info.plan_source === 'beta' && betaEnd !== null) return predicted(betaEnd);
  // Gift / admin plan: from its end.
  const end = toTime(info.current_period_end);
  if (end !== null && status !== 'cancelled') return predicted(end);
  return null;
}

/**
 * The grace end already passed but the nightly job hasn't suspended the
 * account yet — say "tonight", never show a date in the past.
 */
export function graceEndPassed(info: BillingInfo | null | undefined, now: Date = new Date()): boolean {
  const end = toTime(graceEndsAt(info, now));
  return end !== null && end <= now.getTime();
}

function normalisePath(pathname: string): string {
  const p = (pathname || '/').split('?')[0].split('#')[0];
  return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;
}

export function isLimitedAccessPath(pathname: string): boolean {
  const p = normalisePath(pathname);
  return LIMITED_ACCESS_PATHS.some((allowed) => p === allowed || p.startsWith(`${allowed}/`));
}

export interface AccessDecision {
  /** Render the requested page? */
  allow: boolean;
  level: AccessLevel;
  reason: BlockReason | null;
  /** Where to send the host when allow = false. */
  redirectTo: string | null;
}

/**
 * The dashboard gate. Admins and full-access hosts open every page; hosts in
 * grace open every page too (creation is disabled inside); blocked hosts only
 * open Billing / Account / Support and are sent to Billing (or to Account when
 * the account is waiting to be deleted, where "Undo" lives).
 */
export function decideAccess(
  info: BillingInfo | null | undefined,
  pathname: string,
  now: Date = new Date(),
): AccessDecision {
  const reason = blockReason(info, now);
  const level: AccessLevel = reason === null ? 'full' : reason === 'grace' ? 'grace' : 'blocked';
  if (level !== 'blocked' || isLimitedAccessPath(pathname)) {
    return { allow: true, level, reason, redirectTo: null };
  }
  return {
    allow: false,
    level,
    reason,
    redirectTo: reason === 'deletion_pending' ? '/dashboard/account' : '/dashboard/billing',
  };
}

/** Can the host create properties / units / bookings right now? */
export function canCreate(info: BillingInfo | null | undefined, now: Date = new Date()): boolean {
  return accessLevel(info, now) === 'full';
}

// ─── Plans ────────────────────────────────────────────────────────────────

/** profiles.subscription_plan → our tier key (null = no paid tier). */
export function tierKeyFromPlan(plan: string | null | undefined): PricingTierId | null {
  switch ((plan ?? '').toLowerCase()) {
    case 'early_adopter':
    case 'earlyadopter':
      return 'earlyAdopter';
    case 'starter':
      return 'starter';
    case 'pro':
      return 'pro';
    case 'business':
    case 'enterprise':
      return 'business';
    default:
      return null;
  }
}

/** Human plan name ("Early Adopter", "Pro"…); null for free / none. */
export function planDisplayName(plan: string | null | undefined): string | null {
  const key = (plan ?? '').toLowerCase();
  if (key === 'enterprise') return 'Enterprise';
  const tier = tierKeyFromPlan(plan);
  return tier ? PRICING_TIERS[tier].name : null;
}

/** Price of a tier in cents for an interval. */
export function tierPriceCents(tier: PricingTierId, interval: 'month' | 'year'): number {
  const t = PRICING_TIERS[tier];
  return interval === 'year' ? t.yearlyPrice : t.monthlyPrice;
}

/** Does the host have a live Stripe subscription (renewing or not)? */
export function hasLiveStripeSubscription(info: BillingInfo | null | undefined): boolean {
  if (!info) return false;
  if (typeof info.has_stripe_subscription === 'boolean') return info.has_stripe_subscription;
  return info.plan_source === 'stripe';
}

/** Unit cap of the current plan. */
export function maxUnitsOf(info: BillingInfo | null | undefined): number {
  if (!info) return 0;
  if (typeof info.max_units === 'number') return info.max_units;
  return info.paid_units ?? 0;
}

// ─── Formatting (Croatia: Europe/Zagreb dates, EUR) ──────────────────────

/** "12. 3. 2027." (hr) / "12 March 2027" (en), always on the Zagreb calendar day. */
export function formatBillingDate(iso: string | number | Date | null | undefined, lang: string): string {
  if (iso === null || iso === undefined || iso === '') return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  if (lang === 'hr') {
    const parts = new Intl.DateTimeFormat('hr-HR', {
      day: 'numeric', month: 'numeric', year: 'numeric', timeZone: 'Europe/Zagreb',
    }).formatToParts(d);
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
    return `${get('day')}. ${get('month')}. ${get('year')}.`;
  }
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Zagreb',
  }).format(d);
}

/** "123,45 €" (hr) / "€123.45" (en). Always two decimals — it's money. */
export function formatEurCents(cents: number | null | undefined, lang: string): string {
  const value = (cents ?? 0) / 100;
  return new Intl.NumberFormat(lang === 'hr' ? 'hr-HR' : 'en-IE', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(value);
}

/** Day number of the Zagreb calendar date of an instant. */
function zagrebDayNumber(d: Date): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric', month: 'numeric', day: 'numeric', timeZone: 'Europe/Zagreb',
  }).formatToParts(d);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return Math.round(Date.UTC(get('year'), get('month') - 1, get('day')) / DAY_MS);
}

/**
 * Calendar days (Zagreb) from today until a date: 0 = today, 1 = tomorrow,
 * negative = already past. null when there is no date.
 */
export function daysUntil(iso: string | null | undefined, now: Date = new Date()): number | null {
  const t = toTime(iso);
  if (t === null) return null;
  return zagrebDayNumber(new Date(t)) - zagrebDayNumber(now);
}

/** End date of a plan that does NOT renew by itself (gift / beta / trial / admin). */
export function nonRenewingPlanEnd(info: BillingInfo | null | undefined): string | null {
  if (!info || info.auto_renews) return null;
  const source = info.plan_source;
  if (source === 'trial' || (!source && info.is_trialing)) return info.trial_ends_at ?? null;
  if (source === 'beta') return info.beta_expires_at ?? info.current_period_end ?? null;
  if (source === 'gift' || source === 'admin') return info.current_period_end ?? null;
  return null;
}
